import { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";
import { Box, FormControl, Tooltip, useTheme } from "@mui/material";
import Hub from "@mui/icons-material/Hub";
import { useTranslation } from "react-i18next";
import { ControllerRenderProps } from "react-hook-form";
import { FoodsIcon, FoodsSelectServerPagination, FoodsTabs, FoodsTooltip, Typography } from "@foodpilot/foods";

import { useGetAllIngredientReferencesUnified } from "src/api/client-api/ingredientReferenceUnified/useGetAllIngredientReferencesUnified";
import { useGetOneIngredientReferenceUnified } from "src/api/client-api/ingredientReferenceUnified/useGetOneIngredientReferenceUnified";
import {
    INGREDIENT_REFERENCE_INTENDED_USES,
    IngredientReferenceIntendedUse,
    IngredientReferenceUnified,
    buildCompositeId,
} from "src/api/client-api/ingredientReferenceUnified/types";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { FormFieldLoadError } from "src/components/EntityForm/FormFieldLoadError";
import { FoodsField, FoodsServerSelectField } from "src/components/EntityForm/FoodsFields";

import { IngredientFormType, IngredientReferenceSource } from "../../IngredientForm.types";
import type { IngredientFormData } from "../../IngredientForm.schema";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";

const intendedUseTabValues: (IngredientReferenceIntendedUse | null)[] = [null, ...INGREDIENT_REFERENCE_INTENDED_USES];

type IngredientReferenceFieldProps = {
    appearance?: "legacy" | "product-v2";
    ingredient: IngredientFormType;
    onChange: Dispatch<SetStateAction<IngredientFormType>>;
    field: ControllerRenderProps<IngredientFormData, "ingredientReferenceId">;
};

export const IngredientReferenceField = (props: IngredientReferenceFieldProps) => {
    const { appearance, ingredient, onChange, field } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const featureProductUxV2 = useIsProductUxV2();
    const isProductUxV2 = appearance === "product-v2" || (appearance === undefined && featureProductUxV2);

    const { search, delayedSearch, setSearch } = useDelayedSearch();
    const [intendedUse, setIntendedUse] = useState<IngredientReferenceIntendedUse | null>(null);

    // Single endpoint, both sources merged + sorted server-side. Backend handles graceful
    // degradation if sure-api is down (platform-only response with X-Sure-Api-Degraded: 1).
    const {
        ingredientReferences,
        isError: areReferencesError,
        refetch: refetchReferences,
    } = useGetAllIngredientReferencesUnified({
        q: delayedSearch,
        intendedUse,
        itemsPerPage: 50,
    });

    const intendedUseLabels: Record<IngredientReferenceIntendedUse, string> = {
        human_food: t("ingredientReferences.intendedUse.human_food", "Human food"),
        animal_feed: t("ingredientReferences.intendedUse.animal_feed", "Animal feed"),
    };

    const intendedUseTabs = (
        <FoodsTabs
            tabVariant="buttons"
            forceScrollable
            tabs={intendedUseTabValues.map((use) => ({
                id: use ?? "all",
                name: use === null ? t("ingredientReferences.intendedUse.all", "All uses") : intendedUseLabels[use],
            }))}
            selectedTabIndex={intendedUseTabValues.indexOf(intendedUse)}
            TabProps={{ id: undefined, "aria-controls": undefined }}
            TabsProps={{
                "aria-label": t("ingredientReferences.intendedUse.label", "Use"),
                onChange: (_event, index: number) => setIntendedUse(intendedUseTabValues[index] ?? null),
            }}
        />
    );

    const selectedCompositeId =
        ingredient.ingredientReferenceId !== undefined && ingredient.ingredientReferenceId !== null ?
            buildCompositeId(ingredient.referenceSource, ingredient.ingredientReferenceId)
        :   undefined;

    // Resolve the currently selected option even if it is outside the current search window,
    // so the picker keeps showing the chosen value while typing.
    const {
        ingredientReference: selectedReference,
        isError: isSelectedReferenceError,
        refetch: refetchSelectedReference,
    } = useGetOneIngredientReferenceUnified(selectedCompositeId);

    const isSearchFound = (value: string) => {
        if (delayedSearch === "") return true;
        return value.toLowerCase().includes(delayedSearch.toLowerCase());
    };

    const options = useMemo<IngredientReferenceUnified[]>(() => {
        const merged = [...(ingredientReferences ?? [])];
        if (selectedReference && !merged.some((o) => o.compositeId === selectedReference.compositeId)) {
            merged.push(selectedReference);
        }
        return merged;
    }, [ingredientReferences, selectedReference]);

    const selectedOption = options.find((o) => o.compositeId === selectedCompositeId) ?? null;
    const hasUsablePicker = !areReferencesError || options.length > 0;

    const getOptionId = useCallback(
        (item: IngredientReferenceUnified | null) => (item == null ? "" : item.compositeId),
        [],
    );

    const getSourceIcon = useCallback(
        (item: IngredientReferenceUnified) => {
            if (item.source !== "foodpilot") {
                return <></>;
            }
            return isProductUxV2 ?
                    <FoodsTooltip
                        title={t(
                            "ingredientReferences.referenceSourceTooltipPlatform",
                            "Platform-defined reference (FoodPilot)",
                        )}
                    >
                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                            <FoodsIcon icon="organization" size={2} color={theme.palette.primary.main} />
                        </Box>
                    </FoodsTooltip>
                :   <Tooltip
                        title={t(
                            "ingredientReferences.referenceSourceTooltipPlatform",
                            "Platform-defined reference (FoodPilot)",
                        )}
                        enterDelay={300}
                    >
                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                            <Hub
                                fontSize="small"
                                aria-hidden
                                sx={{ color: theme.palette.primary.main, flexShrink: 0 }}
                            />
                        </Box>
                    </Tooltip>;
        },
        [isProductUxV2, t, theme.palette.primary.main],
    );

    const handleChange = (data: IngredientReferenceUnified | null) => {
        const newReferenceId = data === null ? null : data.id;
        const newSource: IngredientReferenceSource = data?.source ?? "sure";

        field.onChange(newReferenceId);

        onChange((prevState) => ({
            ...prevState,
            ingredientReferenceId: newReferenceId,
            referenceSource: newSource,
        }));
    };

    const loadError = (areReferencesError || isSelectedReferenceError) && (
        <FormFieldLoadError
            message={t("ingredientReferences.loadError", "Ingredient references could not be loaded.")}
            retryLabel={t("common.retry", "Retry")}
            onRetry={() => {
                void refetchReferences();
                if (selectedCompositeId !== undefined) void refetchSelectedReference();
            }}
        />
    );

    if (!isProductUxV2) {
        return (
            <FormControl size="small">
                <Typography variant="body-semibold" sx={{ paddingBottom: theme.spacing(1) }}>
                    {t("ingredient_de_reference_associe")}
                </Typography>
                {loadError}
                {hasUsablePicker && (
                    <FoodsSelectServerPagination
                        onChange={handleChange}
                        options={options}
                        selectedOption={selectedOption}
                        getId={getOptionId}
                        getName={(item) => item?.name ?? ""}
                        getIcon={getSourceIcon}
                        textOptions={{
                            placeholder: t("component.select.placeholder"),
                            searchPlaceholder: t("component.select.searchplaceholder"),
                            emptyOptionsText: t("component.select.nooptions"),
                            defaultValueText: t("component.select.defaultvalue"),
                            noResult: t("component.select.noresultfound"),
                        }}
                        noValue={t("component.select.unselectvalue")}
                        search={search}
                        setSearch={setSearch}
                        isSearchFound={isSearchFound}
                        BoxProps={{ width: "100%" }}
                        BoxOptionsProps={{ width: theme.spacing(56.25) }}
                        filterControls={intendedUseTabs}
                    />
                )}
            </FormControl>
        );
    }

    return (
        <FoodsField label={t("ingredient_de_reference_associe")}>
            {loadError}
            {hasUsablePicker && (
                <FoodsServerSelectField
                    appearance="product-v2"
                    accessibleLabel={t("ingredient_de_reference_associe", "Associated reference ingredient")}
                    onChange={handleChange}
                    options={options}
                    selectedOption={selectedOption}
                    getId={getOptionId}
                    getName={(item) => item?.name ?? ""}
                    getIcon={getSourceIcon}
                    placeholder={t("component.select.placeholder")}
                    searchPlaceholder={t("component.select.searchplaceholder")}
                    emptyOptionsText={t("component.select.nooptions")}
                    defaultValueText={t("component.select.defaultvalue")}
                    noResultText={t("component.select.noresultfound")}
                    noValue={t("component.select.unselectvalue")}
                    search={search}
                    setSearch={setSearch}
                    isSearchFound={isSearchFound}
                    filterControls={intendedUseTabs}
                />
            )}
        </FoodsField>
    );
};
